/**
 * lib/hrLeave.ts
 * ------------------------------------------------------------------
 * HR PAYROLL — leave balance computation (Phase 1, Day 4).
 *
 * Running balances are DERIVED, never stored as a mutable counter:
 *   remaining = entitlement - sum(approved leave days of that type)
 *
 * Entitlements come from the per-staff hrLeaveAllocation when present,
 * otherwise the school defaults (18 annual / 20 sick). The same
 * function feeds the staff profile's Leave tab and the payslip
 * assembly (Day 6), so a balance on a payslip always matches the
 * balance on screen.
 *
 * Leave-request typing is structural on purpose (just {type, days,
 * status}) so this module doesn't depend on the academic-side leave
 * shape and can't create an import cycle.
 * ------------------------------------------------------------------ */
import {
  DEFAULT_LEAVE_ENTITLEMENTS,
  DEFAULT_LEAVE_ACCRUAL_START,
  ACCRUING_LEAVE_TYPES,
  round2,
  type HrLeaveAllocation,
  type LeaveBalance,
} from '@/lib/hrPayroll';

export interface LeaveRequestLike {
  type: string;
  days: number;
  status: string;
  /** ISO start date; used to bucket "taken" by leave year / accrual window. */
  startDate?: string;
}

/**
 * A sale of leave days back to the school, as the balance engine reads it.
 *
 * Structural, like LeaveRequestLike, so this module does not depend on the
 * encashment record and cannot create an import cycle. Only `approved`
 * counts: a pending sale reserves nothing, exactly as a pending leave
 * application reserves nothing.
 */
export interface EncashmentLike {
  type: string;
  days: number;
  status: string;
  /** When it was approved — the date it came off the balance. */
  decidedAt?: string;
}

/** Options controlling accrual-aware balance computation. */
export interface LeaveCalcOptions {
  /**
   * Approved leave SALES. Subtracted from `remaining` and reported as `sold`,
   * never as `taken`, because selling a day is not taking it.
   *
   * Optional, and nothing is subtracted when absent — so every caller that
   * has not been taught about encashment gets exactly the balance it got
   * before. That is the property that makes this safe to ship into a system
   * the pay run reads.
   */
  encashments?: EncashmentLike[];
  alwaysShow?: string[];
  /** Date the opening balances are "as of"; accrual counts from here. */
  accrualStart?: string;
  /** Value the balance at this point in time (default: now). */
  asOf?: Date;
  /** Employee start; the accrual anchor is the later of this and accrualStart. */
  employmentStart?: string;
  /** Current leave year, for resetting non-accruing types (default: asOf year). */
  leaveYear?: number;
  /** Which types accrue monthly and carry over (default: ACCRUING_LEAVE_TYPES). */
  accrualTypes?: string[];
  /**
   * Staff category. Kept for the caller's own use and for resolving a
   * cohort's entitlements — it no longer changes HOW leave is calculated.
   *
   * It used to: 'teaching' switched annual leave to an accumulate-and-reset
   * model tied to the April, August and December holidays, on the reasoning
   * that a teacher's leave IS the school holiday. From 1 October 2026 the
   * school abandoned that. Teachers accrue 21 days monthly and carry them,
   * exactly like everybody else, and there is one engine again.
   */
  category?: string;
}

/**
 * Completed whole months from an anchor date to asOf (never negative) — a
 * month's share is credited once a full month has passed since the anchor
 * (on the 30th, for a 30 June / 30 September balance).
 *
 * KEPT, 21 Oct 2026. HR's paper leave cards credit a month on its first day
 * instead; crediting on the 1st was built and then set aside by the school
 * ("keep the rule that was previously working"). The printed card follows
 * this rule for the current month (lib/leaveCard), so paper and screen agree.
 */
export function monthsAccrued(anchorISO: string, asOf: Date = new Date()): number {
  const a = new Date(anchorISO);
  if (Number.isNaN(a.getTime())) return 0;
  let m = (asOf.getFullYear() - a.getFullYear()) * 12 + (asOf.getMonth() - a.getMonth());
  if (asOf.getDate() < a.getDate()) m -= 1;
  return Math.max(0, m);
}

function yearOf(iso?: string): number | null {
  if (!iso) return null;
  const y = Number(String(iso).slice(0, 4));
  return Number.isFinite(y) ? y : null;
}

function accrualAnchor(opts?: LeaveCalcOptions): string {
  const accrualStart = opts?.accrualStart ?? DEFAULT_LEAVE_ACCRUAL_START;
  return opts?.employmentStart && opts.employmentStart > accrualStart ? opts.employmentStart : accrualStart;
}

/** Months credited for these options, from the later of the cohort anchor and the start date. */
export function monthsFor(opts?: LeaveCalcOptions): number {
  return monthsAccrued(accrualAnchor(opts), opts?.asOf ?? new Date());
}

/** Display order: entitled types first (annual, sick), then the rest. */
const TYPE_ORDER = ['annual', 'sick', 'hospitalisation', 'maternity', 'paternity', 'compassionate', 'study', 'unpaid'];

function orderIndex(type: string): number {
  const i = TYPE_ORDER.indexOf(type);
  return i === -1 ? TYPE_ORDER.length : i;
}

/**
 * The effective entitlements for a staff member: the school defaults, with
 * whatever the allocation sets laid over them, TYPE BY TYPE.
 *
 * It used to be one or the other — any allocation replaced the defaults
 * whole. All 67 allocations (June 2026) set annual leave only, so for every
 * one of those staff sick leave became 0: 15 of them showed a negative sick
 * balance on Leave Applications and My Pay & HR (Oct 2026), and an approval
 * on Leave Applications priced every sick day as UNPAID leave. A type the
 * allocation does not mention keeps the default, as lib/leavePolicy's
 * resolveEntitlements has always done.
 */
export function effectiveEntitlements(
  allocation: HrLeaveAllocation | undefined,
): Record<string, number> {
  return { ...DEFAULT_LEAVE_ENTITLEMENTS, ...(allocation?.entitlements ?? {}) };
}

/**
 * Compute per-type leave balances (accrual-aware).
 *
 * Accruing types (annual): remaining = opening + (entitlement/12) × completed
 * months since the accrual anchor − approved days taken on/after the anchor.
 * The balance carries over indefinitely (it is the same figure paid out on
 * exit). Non-accruing types reset each leave year: remaining = entitlement
 * (+ opening only in the go-live year) − approved days taken this leave year.
 *
 * Leave dated before the accrual anchor is treated as already netted into the
 * opening balance and is not subtracted again. Undated (legacy) requests are
 * counted conservatively. With no options the anchor defaults to
 * DEFAULT_LEAVE_ACCRUAL_START and asOf to now.
 */
/**
 * Teaching annual leave entitlement: 20 days/year under the termly model.
 * The three holiday windows span 10 accruing months (Jan–Apr = 4, Jun–Aug = 3,
 * Oct–Dec = 3), so the monthly accrual rate is 20 / 10 = 2.0 days/month.
 */

export function computeLeaveBalances(
  allocation: HrLeaveAllocation | undefined,
  requests: LeaveRequestLike[],
  opts?: LeaveCalcOptions,
): LeaveBalance[] {
  const entitlements = effectiveEntitlements(allocation);
  const accrualStart = opts?.accrualStart ?? DEFAULT_LEAVE_ACCRUAL_START;
  const asOf = opts?.asOf ?? new Date();
  const accrualTypes = opts?.accrualTypes ?? ACCRUING_LEAVE_TYPES;
  const leaveYear = opts?.leaveYear ?? asOf.getFullYear();
  const months = monthsFor({ ...opts, asOf });
  const accrualStartYear = yearOf(accrualStart);
  const anchorDay = accrualStart.slice(0, 10);

  const types = new Set<string>([
    ...Object.keys(entitlements),
    ...requests.map((r) => r.type),
    ...(opts?.alwaysShow ?? ['annual', 'sick', 'hospitalisation']),
  ]);

  return Array.from(types)
    .map((type) => {
      let entitlement = entitlements[type] ?? 0;
      let opening = Number(allocation?.openingBalances?.[type]) || 0;
      const isAccrual = accrualTypes.includes(type);

      let taken = 0;
      for (const r of requests) {
        if (r.type !== type || r.status !== 'approved') continue;
        // Pre-anchor leave is already netted into the opening — don't double-count.
        const onOrAfterAnchor = !r.startDate || String(r.startDate).slice(0, 10) >= anchorDay;
        if (!onOrAfterAnchor) continue;
        if (!isAccrual) {
          const ry = yearOf(r.startDate);
          if (ry !== null && ry !== leaveYear) continue; // reset annually
        }
        taken += Number(r.days) || 0;
      }

      /* Same anchor and same annual reset as `taken`, so a sale is counted
         in exactly the window a day of leave would have been. */
      let sold = 0;
      for (const e of opts?.encashments ?? []) {
        if (e.type !== type || e.status !== 'approved') continue;
        const onOrAfterAnchor = !e.decidedAt || String(e.decidedAt).slice(0, 10) >= anchorDay;
        if (!onOrAfterAnchor) continue;
        if (!isAccrual) {
          const ey = yearOf(e.decidedAt);
          if (ey !== null && ey !== leaveYear) continue;
        }
        sold += Number(e.days) || 0;
      }

      let remaining: number;
      if (isAccrual) {
        const rate = entitlement / 12;
        remaining = round2(opening + rate * months - taken - sold);
      } else {
        const openingApplies = accrualStartYear === null || leaveYear === accrualStartYear;
        remaining = round2(entitlement + (openingApplies ? opening : 0) - taken - sold);
      }

      return {
        type, entitlement, taken: round2(taken), opening, remaining,
        ...(sold > 0 ? { sold: round2(sold) } : {}),
      };
    })
    .sort((a, b) => orderIndex(a.type) - orderIndex(b.type) || a.type.localeCompare(b.type));
}

/**
 * Inverse of the balance formula: the opening balance to store so that a
 * type's remaining equals `desiredRemaining` right now. Used by the go-live
 * balance editor so a correction stays consistent with accrual.
 */
export function openingFromRemaining(
  type: string,
  desiredRemaining: number,
  ctx: { entitlement: number; taken: number; opts?: LeaveCalcOptions },
): number {
  const accrualTypes = ctx.opts?.accrualTypes ?? ACCRUING_LEAVE_TYPES;
  if (accrualTypes.includes(type)) {
    const months = monthsFor(ctx.opts);
    const rate = ctx.entitlement / 12;
    // remaining = opening + rate·months − taken  ⇒  opening = remaining − rate·months + taken
    return round2(desiredRemaining - rate * months + ctx.taken);
  }
  // reset type: remaining = entitlement + opening − taken  ⇒  opening = remaining − entitlement + taken
  return round2(desiredRemaining - ctx.entitlement + ctx.taken);
}

/**
 * Days of a leave that dock pay: NONE, at this school.
 *
 * The school (21 Oct 2026): leave never costs pay — not leave beyond the
 * entitlement, and not leave recorded as Unpaid. Going over simply takes
 * the balance negative, and it is carried, as HR's leave cards show (Amanda
 * Muller is at −45.76). Until then this priced every day beyond the balance
 * (and every Unpaid day) at a day's basic pay, and approving wrote a salary
 * deduction for it; none was ever created. It returns 0 always; the
 * signature stays so its callers — which remove any old charge linked to a
 * leave when it is edited or cancelled — need not change.
 */
export function unpaidDaysForLeave(
  leave: { id: string; type: string; days: number; status: string },
  allocation: HrLeaveAllocation | undefined,
  allStaffLeave: ({ id: string } & LeaveRequestLike)[],
  /** Days already sold. Without them, leave taken out of days that were
   *  sold would be priced as paid — the same day paid for twice. */
  encashments?: EncashmentLike[],
  /** The person's accrual anchor, start date and leave year (lib/leavePolicy
   *  leaveBasis) — so the check counts from the same date the screens do. */
  opts?: LeaveCalcOptions,
): number {
  void leave; void allocation; void allStaffLeave; void encashments; void opts;
  return 0;
}

/* ==================================================================
   LEAVE-DAY COUNTING (Item 3, rules 1 & 2)
   ------------------------------------------------------------------
   How many leave days a date span actually consumes:
     • a paid public holiday consumes 0 (does not eat into leave)
     • Sunday consumes 0 (non-working)
     • Saturday consumes 0.5 (half working day)
     • Monday–Friday consume 1
   Public holidays are passed in (live from the publicHolidays
   collection) so this stays a pure function with no Firestore/React
   dependency. Dates are handled in UTC to avoid timezone drift on the
   YYYY-MM-DD inputs.
   ================================================================== */

/**
 * Leave types counted in CALENDAR days, every one a full day.
 *
 * Illness does not observe a timetable. A member of staff in hospital from
 * Friday to Monday was in hospital for four days, and counting the Saturday
 * as a half and the Sunday as nothing said they were absent for two and a
 * half — which is not what happened, and made the entitlement mean something
 * different depending on which day of the week somebody fell ill.
 *
 * Public holidays inside such a span count too. A holiday does not consume
 * ANNUAL leave because the staff member would not have been at work anyway
 * and should not lose a day of it; that reasoning does not carry to being
 * ill, where the days are a record of absence rather than of time off owed.
 *
 * MATERNITY TOO (Oct 2026). Maternity leave is a continuous period — so
 * many weeks from a date — not a count of school days. Counting only
 * working days stretched it: a 14-week leave entered as 98 days ran on
 * well past 14 weeks, because each weekend added days instead of using
 * them. Every day of the span is a day of it, weekends and holidays
 * included.
 */
export const CALENDAR_DAY_LEAVE_TYPES = ['sick', 'hospitalisation', 'maternity'] as const;

/** The leave-day weight of a single date (0, 0.5, or 1). */
export function leaveDayWeight(
  d: Date,
  isHoliday: (iso: string) => boolean = () => false,
  type?: string,
): number {
  if (type && (CALENDAR_DAY_LEAVE_TYPES as readonly string[]).includes(type)) return 1;
  const iso = d.toISOString().slice(0, 10);
  if (isHoliday(iso)) return 0;       // paid public holiday — does not consume leave
  const dow = d.getUTCDay();          // 0 = Sun … 6 = Sat
  if (dow === 0) return 0;            // Sunday — non-working
  if (dow === 6) return 0.5;          // Saturday — half day
  return 1;                            // Monday–Friday — full day
}

/**
 * Leave days consumed by an inclusive date span [start, end].
 * `isHoliday(iso)` should report the live public-holiday set. Returns a
 * value rounded to the nearest half-day. 0 if the dates are invalid or
 * reversed.
 */
export function countLeaveDays(
  start: string,
  end: string,
  isHoliday: (iso: string) => boolean = () => false,
  type?: string,
): number {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  let cur = Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
  const last = Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate());
  if (last < cur) return 0;
  let total = 0;
  let guard = 0;
  while (cur <= last && guard < 1000) {
    total += leaveDayWeight(new Date(cur), isHoliday, type);
    cur += 86400000;
    guard++;
  }
  return Math.round(total * 2) / 2;
}
